/**
 * Vegetation species definitions.
 *
 * A species is picked per placement cell from the list its biome permits, the
 * same "classifier decides, data does not paint a map" philosophy as
 * `BiomeDef.ts`. Land species are keyed by biome id in `BIOME_VEGETATION`;
 * aquatic species (kelp, seagrass) are kept separately in
 * `UNDERWATER_VEGETATION` because they are gated on depth below sea level
 * rather than on the land climate bands.
 *
 * Biomes with `vegetationDensity === 0` in `BiomeDef.ts` (volcanic, glacier,
 * regolith, dunes, ...) intentionally have no entry here and are skipped by
 * `vegetationInstanceAt` automatically.
 */

export type VegetationType = 'tree' | 'bush' | 'grass' | 'flower' | 'kelp' | 'seagrass' | 'rock' | 'crystal';

/**
 * Ground cover is deliberately treated as a separate population from the
 * larger silhouettes.  It gets a reserved share of the same per-chunk
 * instance budget, so a dense tree canopy cannot accidentally consume every
 * slot before grass and flowers are considered.
 */
export function isGroundCoverType(type: VegetationType): boolean {
  return type === 'grass' || type === 'flower' || type === 'seagrass';
}

export interface VegetationSpeciesDef {
  id: string;
  type: VegetationType;
  /** Base colour, linear RGB. */
  color: [number, number, number];
  /** Secondary colour blended in per instance for variation. */
  detailColor: [number, number, number];
  /** Overall height range, metres. */
  heightRange: [number, number];
  /** Canopy/blade radius range, metres. */
  radiusRange: [number, number];
  /** Relative pick chance among the species permitted for its biome. */
  weight: number;
  /** Maximum ground slope this species can take root on, radians. */
  maxSlope: number;
  /** Aquatic species only: maximum depth below sea level, metres. */
  maxDepth?: number;
}

const DEG = Math.PI / 180;

export const BIOME_VEGETATION: Record<string, VegetationSpeciesDef[]> = {
  temperateForest: [
    { id: 'broadleafTree', type: 'tree', color: [0.16, 0.3, 0.13], detailColor: [0.26, 0.4, 0.18], heightRange: [8, 18], radiusRange: [2.5, 5], weight: 1.4, maxSlope: 26 * DEG },
    { id: 'conifer', type: 'tree', color: [0.09, 0.22, 0.14], detailColor: [0.14, 0.3, 0.2], heightRange: [10, 22], radiusRange: [1.8, 3.2], weight: 1, maxSlope: 32 * DEG },
    { id: 'forestBush', type: 'bush', color: [0.2, 0.32, 0.14], detailColor: [0.3, 0.4, 0.2], heightRange: [0.6, 1.6], radiusRange: [0.5, 1.1], weight: 1.2, maxSlope: 34 * DEG },
    { id: 'forestGrass', type: 'grass', color: [0.34, 0.4, 0.18], detailColor: [0.44, 0.48, 0.24], heightRange: [0.2, 0.5], radiusRange: [0.25, 0.4], weight: 2, maxSlope: 34 * DEG },
    { id: 'forestFlower', type: 'flower', color: [0.8, 0.78, 0.3], detailColor: [0.9, 0.5, 0.55], heightRange: [0.15, 0.3], radiusRange: [0.1, 0.18], weight: 0.35, maxSlope: 30 * DEG },
  ],
  rainforest: [
    { id: 'canopyTree', type: 'tree', color: [0.08, 0.22, 0.07], detailColor: [0.14, 0.3, 0.1], heightRange: [16, 34], radiusRange: [3, 6], weight: 1.3, maxSlope: 30 * DEG },
    { id: 'rainforestBush', type: 'bush', color: [0.1, 0.24, 0.08], detailColor: [0.18, 0.32, 0.12], heightRange: [0.8, 2.2], radiusRange: [0.7, 1.4], weight: 1.4, maxSlope: 36 * DEG },
    { id: 'rainforestFern', type: 'grass', color: [0.13, 0.26, 0.1], detailColor: [0.2, 0.34, 0.14], heightRange: [0.4, 0.9], radiusRange: [0.4, 0.7], weight: 1.6, maxSlope: 38 * DEG },
  ],
  jungleCanopy: [
    { id: 'emergentTree', type: 'tree', color: [0.06, 0.19, 0.06], detailColor: [0.11, 0.27, 0.09], heightRange: [30, 55], radiusRange: [4, 8], weight: 1.2, maxSlope: 26 * DEG },
    { id: 'canopyBush', type: 'bush', color: [0.08, 0.21, 0.07], detailColor: [0.14, 0.28, 0.1], heightRange: [1, 2.6], radiusRange: [0.8, 1.6], weight: 1.3, maxSlope: 30 * DEG },
    { id: 'jungleFern', type: 'grass', color: [0.1, 0.23, 0.08], detailColor: [0.17, 0.3, 0.12], heightRange: [0.5, 1.1], radiusRange: [0.45, 0.8], weight: 1.5, maxSlope: 30 * DEG },
  ],
  grassland: [
    { id: 'plainsGrass', type: 'grass', color: [0.36, 0.4, 0.2], detailColor: [0.46, 0.48, 0.26], heightRange: [0.25, 0.6], radiusRange: [0.3, 0.5], weight: 2.4, maxSlope: 20 * DEG },
    { id: 'plainsBush', type: 'bush', color: [0.3, 0.36, 0.18], detailColor: [0.4, 0.42, 0.22], heightRange: [0.7, 1.4], radiusRange: [0.6, 1], weight: 0.8, maxSlope: 20 * DEG },
    { id: 'loneTree', type: 'tree', color: [0.2, 0.3, 0.14], detailColor: [0.3, 0.38, 0.18], heightRange: [6, 12], radiusRange: [2, 4], weight: 0.25, maxSlope: 18 * DEG },
    { id: 'meadowFlower', type: 'flower', color: [0.85, 0.82, 0.35], detailColor: [0.75, 0.35, 0.5], heightRange: [0.15, 0.35], radiusRange: [0.1, 0.2], weight: 0.5, maxSlope: 20 * DEG },
  ],
  savannah: [
    { id: 'acaciaTree', type: 'tree', color: [0.28, 0.32, 0.13], detailColor: [0.38, 0.4, 0.18], heightRange: [5, 11], radiusRange: [2.5, 5], weight: 0.6, maxSlope: 18 * DEG },
    { id: 'savannahGrass', type: 'grass', color: [0.44, 0.4, 0.2], detailColor: [0.54, 0.48, 0.26], heightRange: [0.3, 0.7], radiusRange: [0.3, 0.5], weight: 2.6, maxSlope: 20 * DEG },
    { id: 'dryBush', type: 'bush', color: [0.36, 0.34, 0.16], detailColor: [0.46, 0.42, 0.2], heightRange: [0.6, 1.3], radiusRange: [0.5, 0.9], weight: 1, maxSlope: 22 * DEG },
    { id: 'savannahFlower', type: 'flower', color: [0.78, 0.7, 0.3], detailColor: [0.85, 0.45, 0.2], heightRange: [0.15, 0.3], radiusRange: [0.1, 0.18], weight: 0.25, maxSlope: 20 * DEG },
  ],
  swamp: [
    { id: 'cypressTree', type: 'tree', color: [0.15, 0.22, 0.11], detailColor: [0.22, 0.28, 0.15], heightRange: [7, 16], radiusRange: [1.6, 3], weight: 1, maxSlope: 6 * DEG },
    { id: 'reed', type: 'grass', color: [0.22, 0.26, 0.13], detailColor: [0.3, 0.32, 0.17], heightRange: [0.8, 1.8], radiusRange: [0.15, 0.3], weight: 2.2, maxSlope: 8 * DEG },
    { id: 'swampBush', type: 'bush', color: [0.16, 0.22, 0.11], detailColor: [0.24, 0.28, 0.15], heightRange: [0.6, 1.4], radiusRange: [0.6, 1.1], weight: 1.2, maxSlope: 8 * DEG },
    { id: 'swampFlower', type: 'flower', color: [0.7, 0.62, 0.7], detailColor: [0.85, 0.4, 0.55], heightRange: [0.2, 0.4], radiusRange: [0.12, 0.2], weight: 0.3, maxSlope: 8 * DEG },
  ],
  tundra: [
    { id: 'dwarfShrub', type: 'bush', color: [0.32, 0.34, 0.24], detailColor: [0.42, 0.42, 0.3], heightRange: [0.2, 0.5], radiusRange: [0.35, 0.6], weight: 1.4, maxSlope: 22 * DEG },
    { id: 'tundraLichen', type: 'grass', color: [0.4, 0.42, 0.3], detailColor: [0.5, 0.5, 0.36], heightRange: [0.06, 0.15], radiusRange: [0.2, 0.35], weight: 1.6, maxSlope: 24 * DEG },
    { id: 'tundraFlower', type: 'flower', color: [0.75, 0.75, 0.7], detailColor: [0.6, 0.5, 0.75], heightRange: [0.08, 0.16], radiusRange: [0.08, 0.14], weight: 0.3, maxSlope: 22 * DEG },
  ],
  alpine: [
    { id: 'alpineShrub', type: 'bush', color: [0.3, 0.3, 0.24], detailColor: [0.4, 0.4, 0.32], heightRange: [0.15, 0.4], radiusRange: [0.3, 0.5], weight: 1, maxSlope: 34 * DEG },
    { id: 'alpineTuft', type: 'grass', color: [0.36, 0.36, 0.26], detailColor: [0.46, 0.46, 0.32], heightRange: [0.08, 0.2], radiusRange: [0.18, 0.3], weight: 1.2, maxSlope: 38 * DEG },
    { id: 'alpineFlower', type: 'flower', color: [0.8, 0.78, 0.85], detailColor: [0.5, 0.4, 0.75], heightRange: [0.06, 0.12], radiusRange: [0.06, 0.1], weight: 0.3, maxSlope: 36 * DEG },
  ],
  karst: [
    { id: 'karstTree', type: 'tree', color: [0.18, 0.28, 0.15], detailColor: [0.26, 0.36, 0.2], heightRange: [5, 10], radiusRange: [1.8, 3.2], weight: 0.9, maxSlope: 40 * DEG },
    { id: 'karstBush', type: 'bush', color: [0.22, 0.3, 0.16], detailColor: [0.3, 0.38, 0.2], heightRange: [0.5, 1.1], radiusRange: [0.5, 0.9], weight: 1.2, maxSlope: 46 * DEG },
    { id: 'karstGrass', type: 'grass', color: [0.32, 0.36, 0.2], detailColor: [0.4, 0.44, 0.24], heightRange: [0.2, 0.45], radiusRange: [0.25, 0.4], weight: 1.5, maxSlope: 46 * DEG },
  ],
  fungal: [
    { id: 'fungalStalk', type: 'tree', color: [0.34, 0.2, 0.3], detailColor: [0.46, 0.28, 0.4], heightRange: [3, 9], radiusRange: [1, 2.4], weight: 1.1, maxSlope: 16 * DEG },
    { id: 'fungalMat', type: 'grass', color: [0.3, 0.16, 0.26], detailColor: [0.42, 0.24, 0.36], heightRange: [0.15, 0.4], radiusRange: [0.3, 0.55], weight: 1.8, maxSlope: 18 * DEG },
  ],
  alienOrganic: [
    { id: 'alienStalk', type: 'tree', color: [0.16, 0.28, 0.32], detailColor: [0.24, 0.4, 0.44], heightRange: [4, 12], radiusRange: [1.2, 2.6], weight: 1, maxSlope: 28 * DEG },
    { id: 'alienFrond', type: 'bush', color: [0.2, 0.32, 0.34], detailColor: [0.3, 0.44, 0.46], heightRange: [0.5, 1.3], radiusRange: [0.5, 1], weight: 1.3, maxSlope: 30 * DEG },
  ],
  coastal: [
    { id: 'palmTree', type: 'tree', color: [0.24, 0.32, 0.14], detailColor: [0.34, 0.4, 0.2], heightRange: [6, 12], radiusRange: [1.4, 2.4], weight: 0.5, maxSlope: 14 * DEG },
    { id: 'coastalGrass', type: 'grass', color: [0.4, 0.42, 0.22], detailColor: [0.5, 0.5, 0.28], heightRange: [0.2, 0.45], radiusRange: [0.25, 0.4], weight: 1.4, maxSlope: 16 * DEG },
    { id: 'coastalBush', type: 'bush', color: [0.32, 0.36, 0.18], detailColor: [0.42, 0.44, 0.24], heightRange: [0.4, 0.9], radiusRange: [0.4, 0.7], weight: 0.8, maxSlope: 18 * DEG },
  ],
  desert: [
    { id: 'desertScrub', type: 'bush', color: [0.42, 0.36, 0.2], detailColor: [0.52, 0.44, 0.26], heightRange: [0.3, 0.7], radiusRange: [0.4, 0.7], weight: 1.2, maxSlope: 20 * DEG },
    { id: 'cactus', type: 'bush', color: [0.24, 0.36, 0.22], detailColor: [0.32, 0.44, 0.28], heightRange: [0.6, 1.8], radiusRange: [0.2, 0.4], weight: 0.6, maxSlope: 22 * DEG },
  ],
  rockyDesert: [
    { id: 'sparseScrub', type: 'bush', color: [0.3, 0.28, 0.22], detailColor: [0.4, 0.36, 0.28], heightRange: [0.2, 0.5], radiusRange: [0.3, 0.5], weight: 1, maxSlope: 30 * DEG },
  ],
  cave: [
    { id: 'caveFungus', type: 'bush', color: [0.22, 0.26, 0.28], detailColor: [0.3, 0.4, 0.42], heightRange: [0.15, 0.4], radiusRange: [0.25, 0.45], weight: 1, maxSlope: 40 * DEG },
  ],

  // Barren biomes get rock or crystal scatter instead of vegetation, reusing
  // the same species/scatter/instancing machinery — a 'species' here is just
  // a shape, density and colour, not necessarily something alive.
  volcanic: [
    { id: 'basaltBoulder', type: 'rock', color: [0.14, 0.13, 0.13], detailColor: [0.22, 0.18, 0.17], heightRange: [0.8, 2.4], radiusRange: [0.7, 1.8], weight: 1, maxSlope: 48 * DEG },
  ],
  ashPlain: [
    { id: 'ashBoulder', type: 'rock', color: [0.28, 0.27, 0.26], detailColor: [0.36, 0.34, 0.32], heightRange: [0.5, 1.6], radiusRange: [0.5, 1.3], weight: 1, maxSlope: 40 * DEG },
  ],
  obsidianFlow: [
    { id: 'obsidianShard', type: 'crystal', color: [0.05, 0.05, 0.07], detailColor: [0.14, 0.13, 0.18], heightRange: [0.6, 2.2], radiusRange: [0.3, 0.8], weight: 1, maxSlope: 44 * DEG },
  ],
  regolith: [
    { id: 'regolithBoulder', type: 'rock', color: [0.42, 0.4, 0.37], detailColor: [0.5, 0.48, 0.44], heightRange: [0.4, 1.8], radiusRange: [0.4, 1.4], weight: 1, maxSlope: 50 * DEG },
  ],
  crystalField: [
    { id: 'quartzCluster', type: 'crystal', color: [0.55, 0.75, 0.85], detailColor: [0.75, 0.55, 0.85], heightRange: [1, 3.2], radiusRange: [0.5, 1.2], weight: 1.2, maxSlope: 42 * DEG },
    { id: 'amethystSpike', type: 'crystal', color: [0.5, 0.35, 0.75], detailColor: [0.68, 0.5, 0.88], heightRange: [0.6, 2], radiusRange: [0.3, 0.7], weight: 1, maxSlope: 42 * DEG },
  ],
  glacier: [
    { id: 'iceSpike', type: 'crystal', color: [0.75, 0.88, 0.95], detailColor: [0.85, 0.94, 1], heightRange: [1, 3.4], radiusRange: [0.4, 1], weight: 1, maxSlope: 46 * DEG },
  ],
  iceField: [
    { id: 'iceBoulder', type: 'rock', color: [0.7, 0.84, 0.92], detailColor: [0.82, 0.92, 0.98], heightRange: [0.5, 1.6], radiusRange: [0.5, 1.2], weight: 1, maxSlope: 40 * DEG },
  ],
  saltFlat: [
    { id: 'saltCrystal', type: 'crystal', color: [0.88, 0.87, 0.82], detailColor: [0.95, 0.94, 0.9], heightRange: [0.3, 0.9], radiusRange: [0.3, 0.7], weight: 1, maxSlope: 20 * DEG },
  ],
};

export const UNDERWATER_VEGETATION: Record<string, VegetationSpeciesDef[]> = {
  ocean: [
    { id: 'kelp', type: 'kelp', color: [0.1, 0.24, 0.16], detailColor: [0.16, 0.32, 0.2], heightRange: [4, 14], radiusRange: [0.3, 0.6], weight: 1, maxSlope: 30 * DEG, maxDepth: 25 },
    { id: 'seagrass', type: 'seagrass', color: [0.14, 0.3, 0.18], detailColor: [0.2, 0.38, 0.22], heightRange: [0.3, 0.8], radiusRange: [0.2, 0.35], weight: 1.6, maxSlope: 10 * DEG, maxDepth: 8 },
  ],
};

/**
 * Underwater vegetation instances per square kilometre, keyed by biome id.
 *
 * `BiomeDef.vegetationDensity` is deliberately zero for `ocean` (it means
 * "land vegetation," and the sea floor has none) so aquatic flora needs its
 * own density independent of that field. Depth gating happens per species via
 * `maxDepth`, not here — this is the density within the photic band before
 * any individual species' depth limit is applied.
 */
export const UNDERWATER_DENSITY: Record<string, number> = {
  ocean: 2200,
};

const BIOME_VEGETATION_INDEX = new Map<string, Map<string, number>>();
for (const [biomeId, species] of Object.entries(BIOME_VEGETATION)) {
  BIOME_VEGETATION_INDEX.set(biomeId, new Map(species.map((s, i) => [s.id, i])));
}

/** All species across every biome, in a stable global order for instance packing. */
export const ALL_SPECIES: VegetationSpeciesDef[] = (() => {
  const seen = new Set<string>();
  const all: VegetationSpeciesDef[] = [];
  for (const list of Object.values(BIOME_VEGETATION)) {
    for (const s of list) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      all.push(s);
    }
  }
  for (const list of Object.values(UNDERWATER_VEGETATION)) {
    for (const s of list) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      all.push(s);
    }
  }
  return all;
})();

export const SPECIES_INDEX = new Map(ALL_SPECIES.map((s, i) => [s.id, i]));

export function getSpecies(id: string): VegetationSpeciesDef {
  const s = ALL_SPECIES[SPECIES_INDEX.get(id) ?? -1];
  if (!s) throw new Error(`Unknown vegetation species "${id}"`);
  return s;
}
